"use client";

import { Trans } from "@lingui/react/macro";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const INTERACTIVE_LAYER_SELECTORS = [
  "[data-radix-dropdown-menu-content]",
  "[data-radix-dropdown-menu-subcontent]",
  "[data-portal-select-content]",
  "[data-radix-popover-content]",
  "[data-radix-select-content]",
  "[data-radix-hover-card-content]",
  "[data-radix-tooltip-content]",
  "[data-radix-context-menu-content]",
  "[data-radix-menubar-content]",
  "[data-radix-navigation-menu-content]",
  "[data-radix-popper-content-wrapper]",
] as const;

const isElement = (value: EventTarget | null): value is Element =>
  value instanceof Element;

const isInsideInteractiveLayer = (target: EventTarget | null) => {
  if (!isElement(target)) return false;

  return INTERACTIVE_LAYER_SELECTORS.some((selector) =>
    target.closest(selector),
  );
};

/**
 * Radix系ポータルが開いているかどうかを判定し、閉じ動作を抑止するために利用します。
 */
const hasOpenInteractiveLayer = () => {
  if (typeof document === "undefined") return false;

  return INTERACTIVE_LAYER_SELECTORS.some((selector) =>
    Array.from(document.querySelectorAll(selector)).some((element) => {
      const dataState = element.getAttribute("data-state");
      if (dataState) {
        return dataState !== "closed";
      }

      const ariaHidden = element.getAttribute("aria-hidden");
      if (ariaHidden === "true") {
        return false;
      }

      const dataAriaHidden = element.getAttribute("data-aria-hidden");
      if (dataAriaHidden === "true") {
        return false;
      }

      return true;
    }),
  );
};

/**
 * ダイアログ外での操作が閉じるべきでない状況かどうかを判定します。
 * レイヤー内での操作、または他インタラクティブレイヤーが開いた状態でのオーバーレイ操作を抑止します。
 * @param target ユーザーが操作した要素。
 */
const shouldSkipDismiss = (target: EventTarget | null) => {
  if (isInsideInteractiveLayer(target)) {
    return true;
  }

  if (!isElement(target)) return false;

  const interactedWithOverlay =
    target.getAttribute("data-slot") === "dialog-overlay";

  if (interactedWithOverlay && hasOpenInteractiveLayer()) {
    return true;
  }

  return false;
};

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  onClick,
  shouldIgnoreCloseRef,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay> & {
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  shouldIgnoreCloseRef?: React.MutableRefObject<boolean>;
}) {
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (shouldIgnoreCloseRef?.current) {
      shouldIgnoreCloseRef.current = false;
      event.preventDefault();
      return;
    }

    if (shouldSkipDismiss(event.target)) {
      event.preventDefault();
      return;
    }

    onClick?.(event);
  };

  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className,
      )}
      onClick={handleClick}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  onClose,
  onInteractOutside,
  ...props
}: Omit<
  React.ComponentProps<typeof DialogPrimitive.Content>,
  "onInteractOutside"
> & {
  showCloseButton?: boolean;
  onClose?: () => void;
  onInteractOutside?: (
    event: React.ComponentProps<
      typeof DialogPrimitive.Content
    >["onInteractOutside"] extends
      | ((event: infer EventType) => void)
      | undefined
      ? EventType
      : never,
  ) => void;
}) {
  const ignoreDismissRef = React.useRef(false);

  React.useEffect(() => {
    const handlePointerDownCapture = (event: PointerEvent) => {
      if (isInsideInteractiveLayer(event.target)) {
        ignoreDismissRef.current = true;
        return;
      }

      if (
        isElement(event.target) &&
        event.target.getAttribute("data-slot") === "dialog-overlay" &&
        hasOpenInteractiveLayer()
      ) {
        ignoreDismissRef.current = true;
        return;
      }

      ignoreDismissRef.current = false;
    };

    window.addEventListener("pointerdown", handlePointerDownCapture, true);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDownCapture, true);
    };
  }, []);

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay
        onClick={() => {
          onClose?.();
        }}
        shouldIgnoreCloseRef={ignoreDismissRef}
      />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200",
          className,
        )}
        onInteractOutside={(event) => {
          onInteractOutside?.(event);
          if (event.defaultPrevented) return;

          if (ignoreDismissRef.current || shouldSkipDismiss(event.target)) {
            event.preventDefault();
          }

          ignoreDismissRef.current = false;
        }}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            onClick={() => {
              onClose?.();
            }}
            className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className="sr-only">
              <Trans>Close</Trans>
            </span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
};
